/**
 * Constants for cross-frame messaging between FinSuite (parent) and embedded applications (child iframe).
 *
 * This implements a handshake protocol:
 * 1. Embedded app sends FINSUITE_EMBED_READY to parent on mount
 * 2. Parent tracks readiness and uses postMessage for navigation instead of iframe remount
 * 3. Embedded app listens for commands (e.g., NAVIGATE_HOME) and routes internally
 *
 * Any embedded application that implements this protocol will benefit from seamless
 * navigation without iframe destruction/recreation.
 */

/** Message types sent FROM the embedded app TO the parent (FinSuite) */
export const EMBED_TO_PARENT_MESSAGES = {
  /** Signals that the embedded app is ready to receive navigation commands */
  EMBED_READY: 'FINSUITE_EMBED_READY',
  /** Embedded app declares search capability and configuration */
  SEARCH_CAPABILITIES: 'FINSUITE_SEARCH_CAPABILITIES',
  /** Embedded app returns search results for a query */
  SEARCH_RESULTS: 'FINSUITE_SEARCH_RESULTS',
  /** Embedded app reports outcome of an action (e.g. favorite toggle) */
  SEARCH_ACTION_RESULT: 'FINSUITE_SEARCH_ACTION_RESULT',
  /** Embedded app notifies parent that a click occurred inside the iframe */
  IFRAME_CLICK: 'FINSUITE_IFRAME_CLICK'
} as const;

/** Message types sent FROM the parent (FinSuite) TO the embedded app */
export const PARENT_TO_EMBED_MESSAGES = {
  /** Commands the embedded app to navigate to its home/landing page */
  NAVIGATE_HOME: 'FINSUITE_NAVIGATE_HOME',
  /** Commands the embedded app to switch between light and dark mode. Payload: { isDarkMode: boolean } */
  THEME_CHANGE: 'FINSUITE_THEME_CHANGE',
  /** Forwards user's search query to the embedded app */
  SEARCH_QUERY: 'FINSUITE_SEARCH_QUERY',
  /** User clicked a search result — embedded app should navigate */
  SEARCH_RESULT_CLICK: 'FINSUITE_SEARCH_RESULT_CLICK',
  /** User triggered an action on a search result (e.g. toggle-favorite) */
  SEARCH_ACTION: 'FINSUITE_SEARCH_ACTION'
} as const;
